import React, { useState } from 'react';
import { extractErrorMessage } from '../lib/errors';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Check, Sparkles, Lock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { useAuth } from '../context/AuthContext';

export default function Checkout() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [showPromo, setShowPromo] = useState(false);
  const [promo, setPromo] = useState('');
  const [promoState, setPromoState] = useState({ status: 'idle', description: null });
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState({ number: '', exp: '', cvc: '', name: '' });

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-5">
        <Card className="bg-[#111110] border-zinc-900 p-8 text-center max-w-md">
          <div className="font-display text-2xl">Sign in to upgrade</div>
          <p className="text-sm text-zinc-500 mt-2">Create an account or sign in to continue to checkout.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={()=>nav('/login?next=/checkout')}>Sign in</Button>
            <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black" onClick={()=>nav('/signup?next=/checkout')}>Create account</Button>
          </div>
        </Card>
      </div>
    );
  }

  const validatePromo = async () => {
    if (!promo.trim()) return;
    setPromoState({ status: 'checking', description: null });
    try {
      const r = await api.post('/billing/validate-promo', { code: promo });
      if (r.data.valid) {
        setPromoState({ status: 'valid', description: r.data.effect_description });
        toast.success('Promo code applied');
      } else {
        setPromoState({ status: 'invalid', description: null });
        toast.error('Invalid or expired promo code');
      }
    } catch (err) {
      setPromoState({ status: 'invalid', description: null });
      toast.error('Could not validate code');
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const r = await api.post('/billing/checkout', { plan: 'pro', promo_code: promo || undefined });
      if (r.data.status === 'active') {
        await refresh();
        toast.success('Pro activated');
        nav('/app?upgraded=1');
      } else if (r.data.status === 'requires_payment') {
        // For MVP, we don't have Stripe wired. If a percent-off promo was applied, show info.
        toast.error(r.data.message || 'Card payment is not enabled yet. Try promo code JJoyUnlim1 for free access.');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Checkout failed'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-zinc-100">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
        <Link to="/" className="text-sm text-zinc-500 hover:text-[#f5d67a] flex items-center gap-1"><ArrowLeft size={14}/>Back</Link>
        <div className="mt-4 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full border border-[#d4a94a] flex items-center justify-center"><span className="font-display text-[#d4a94a] text-lg">R</span></div>
          <span className="font-display text-xl">Reachly Checkout</span>
        </div>

        <div className="mt-8 grid md:grid-cols-5 gap-8">
          {/* Left: order summary */}
          <div className="md:col-span-2">
            <Card className="bg-[#111110] border-zinc-900 p-6">
              <div className="text-xs uppercase tracking-widest text-[#d4a94a]">Order summary</div>
              <div className="mt-3 flex items-center gap-2">
                <Sparkles size={16} className="text-[#d4a94a]"/>
                <span className="font-display text-xl">Reachly Pro</span>
                <Badge className="bg-[#1a1815] text-[#f5d67a] border border-[#3a2f18]">Monthly</Badge>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-zinc-300">
                {['Unlimited campaigns','200 emails / day','AI CV parsing','Advanced analytics','Priority support'].map(f=>(
                  <li key={f} className="flex gap-2"><Check size={14} className="text-[#d4a94a]"/>{f}</li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-zinc-900 space-y-2 text-sm">
                <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>$19.00</span></div>
                {promoState.status === 'valid' && (
                  <div className="flex justify-between text-emerald-400"><span>Promo applied</span><span>{promoState.description}</span></div>
                )}
                <div className="flex justify-between text-lg pt-2 border-t border-zinc-900">
                  <span className="text-zinc-300">Total today</span>
                  <span className="font-display gold-text">
                    {promoState.status === 'valid' && (promoState.description || '').toLowerCase().includes('free') ? '$0.00' : '$19.00'}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Right: payment form */}
          <div className="md:col-span-3">
            <Card className="bg-[#111110] border-zinc-900 p-6">
              <div className="font-display text-xl">Payment</div>
              <p className="text-sm text-zinc-500 mt-1">Secure checkout. Cancel any time.</p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-zinc-300">Cardholder name</Label>
                  <Input value={card.name} onChange={e=>setCard({...card, name:e.target.value})} className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]" placeholder="Alex Morgan"/>
                </div>
                <div>
                  <Label className="text-zinc-300">Card number</Label>
                  <Input value={card.number} onChange={e=>setCard({...card, number:e.target.value})} className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]" placeholder="1234 1234 1234 1234"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300">Expiry</Label>
                    <Input value={card.exp} onChange={e=>setCard({...card, exp:e.target.value})} className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]" placeholder="MM/YY"/>
                  </div>
                  <div>
                    <Label className="text-zinc-300">CVC</Label>
                    <Input value={card.cvc} onChange={e=>setCard({...card, cvc:e.target.value})} className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]" placeholder="123"/>
                  </div>
                </div>

                {/* Promo code section */}
                <div className="pt-4 border-t border-zinc-900">
                  {!showPromo ? (
                    <button type="button" onClick={()=>setShowPromo(true)} className="text-sm text-[#f5d67a] hover:underline">Have a promo code?</button>
                  ) : (
                    <div>
                      <Label className="text-zinc-300">Promo code</Label>
                      <div className="mt-1.5 flex gap-2">
                        <Input value={promo} onChange={e=>{setPromo(e.target.value); setPromoState({status:'idle', description:null});}} className="bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] uppercase" placeholder="Enter code"/>
                        <Button type="button" onClick={validatePromo} disabled={promoState.status==='checking' || !promo.trim()} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100">Apply</Button>
                      </div>
                      {promoState.status === 'valid' && (
                        <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1"><Check size={12}/>{promoState.description}</div>
                      )}
                      {promoState.status === 'invalid' && (
                        <div className="mt-2 text-xs text-red-400">Invalid or expired code.</div>
                      )}
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium h-11">
                  <Lock size={14} className="mr-2"/>
                  {loading ? 'Processing…' : (promoState.status==='valid' && (promoState.description||'').toLowerCase().includes('free') ? 'Activate Pro (free)' : 'Pay $19 and start')}
                </Button>
                <p className="text-xs text-zinc-500 text-center">By continuing you agree to Reachly’s Terms and Privacy Policy.</p>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
